<template>
  <div class="modal fade" id="topUpModal" tabindex="-1" aria-labelledby="topUpModalLabel" aria-hidden="true" data-bs-backdrop="static">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-warning text-dark">
          <h5 class="modal-title">
            <i class="bx bxs-upvote me-2"></i>Top Up Business Permit
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-warning" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-3">Loading business details...</p>
          </div>

          <div v-else>
            <!-- Business Summary -->
            <div class="card border-0 bg-light mb-4">
              <div class="card-body">
                <h6 class="text-muted mb-3">Current Business Details</h6>
                <div class="row">
                  <div class="col-md-6">
                    <p class="mb-2"><strong>Business Name:</strong> {{ businessDetails.businessName }}</p>
                    <p class="mb-2"><strong>Business ID:</strong> {{ businessDetails.businessID }}</p>
                    <p class="mb-0"><strong>Owner:</strong> {{ businessDetails.fullNames }}</p>
                  </div>
                  <div class="col-md-6">
                    <p class="mb-2"><strong>Current Bill:</strong> {{ currentBillNo }}</p>
                    <p class="mb-2"><strong>Current Amount:</strong> KES {{ numberFormat(currentAmount) }}</p>
                    <p class="mb-0"><strong>Status:</strong>
                      <span class="badge bg-success">{{ businessDetails.stageStatus }}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <form @submit.prevent="processTopUp">
              <!-- Top Up Type -->
              <div class="mb-3">
                <label class="form-label">Top Up Type <span class="text-danger">*</span></label>
                <select v-model="topUpType" @change="calculateTopUp" class="form-control" required>
                  <option value="">Select Top Up Type</option>
                  <option value="monthly">Monthly Extension</option>
                  <option value="quarterly">Quarterly Extension</option>
                  <option value="annual">Annual Extension</option>
                  <option value="custom">Custom Amount</option>
                </select>
              </div>

              <!-- Period Selection (for non-custom) -->
              <div v-if="topUpType && topUpType !== 'custom'" class="mb-3">
                <label class="form-label">Number of {{ topUpType === 'monthly' ? 'Months' : topUpType === 'quarterly' ? 'Quarters' : 'Years' }} <span class="text-danger">*</span></label>
                <input v-model.number="topUpPeriod" @input="calculateTopUp" type="number" min="1" max="12" class="form-control" required>
              </div>

              <!-- Custom Amount -->
              <div v-if="topUpType === 'custom'" class="mb-3">
                <label class="form-label">Custom Amount (KES) <span class="text-danger">*</span></label>
                <input v-model.number="customAmount" @input="calculateTopUp" type="number" min="1" class="form-control" required>
              </div>

              <!-- Fee Breakdown -->
              <div v-if="calculatedAmount > 0" class="card border-0 bg-info bg-soft mb-3">
                <div class="card-body">
                  <h6 class="text-primary mb-3">Top Up Summary</h6>
                  <table class="table table-sm mb-0">
                    <tbody>
                    <tr>
                      <td>Base Amount:</td>
                      <td class="text-end"><strong>KES {{ numberFormat(calculatedAmount) }}</strong></td>
                    </tr>
                    <tr v-if="additionalFees > 0">
                      <td>Additional Fees:</td>
                      <td class="text-end"><strong>KES {{ numberFormat(additionalFees) }}</strong></td>
                    </tr>
                    <tr class="table-active">
                      <td><strong>Total Amount:</strong></td>
                      <td class="text-end"><strong class="text-primary">KES {{ numberFormat(totalAmount) }}</strong></td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- New Expiry Date -->
              <div v-if="newExpiryDate" class="alert alert-info">
                <i class="bx bx-calendar me-2"></i>
                <strong>New Expiry Date:</strong> {{ formattedDate(newExpiryDate) }}
              </div>

              <!-- Notes -->
              <div class="mb-3">
                <label class="form-label">Notes (Optional)</label>
                <textarea v-model="notes" class="form-control" rows="3" placeholder="Add any additional notes..."></textarea>
              </div>

              <div v-if="errorMessage" class="alert alert-danger">
                <i class="bx bx-error me-2"></i>{{ errorMessage }}
              </div>

              <div v-if="successMessage" class="alert alert-success">
                <i class="bx bx-check-circle me-2"></i>{{ successMessage }}
              </div>
            </form>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="processing">
            Cancel
          </button>
          <button type="button" @click="processTopUp" class="btn btn-warning" :disabled="!canSubmit || processing">
            <span v-if="!processing">
              <i class="bx bxs-upvote me-2"></i>Process Top Up (KES {{ numberFormat(totalAmount) }})
            </span>
            <span v-else>
              <span class="spinner-border spinner-border-sm me-2"></span>
              Processing...
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { execute, trade, biller } from "@/api";
import moment from 'moment';

export default {
  name: "TopUpModal",
  props: {
    businessID: String,
    billNo: String,
    feeID: String
  },
  data() {
    return {
      loading: false,
      processing: false,
      errorMessage: '',
      successMessage: '',
      businessDetails: {},
      currentBillNo: '',
      currentAmount: 0,
      currentExpiryDate: null,
      topUpType: '',
      topUpPeriod: 1,
      customAmount: 0,
      calculatedAmount: 0,
      additionalFees: 0,
      totalAmount: 0,
      newExpiryDate: null,
      notes: '',
      baseRate: 0
    };
  },
  computed: {
    canSubmit() {
      return this.topUpType && this.totalAmount > 0 && !this.processing;
    }
  },
  methods: {
    async loadBusinessDetails() {
      if (!this.businessID || !this.billNo) return;

      this.loading = true;
      this.errorMessage = '';
      this.resetForm();

      try {
        const data = new FormData();
        data.append("function", "getBusinessDetails");
        data.append("billNo", this.billNo);
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));
        data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

        const res = await execute(data, trade);
        if (res.data.success) {
          this.businessDetails = res.data.data.business || {};
          this.currentBillNo = this.billNo;

          const billDetails = res.data.data.bill?.billDetails || {};
          this.currentAmount = parseFloat(billDetails.detailAmount || 0);
          this.currentExpiryDate = this.businessDetails.expiryDate || null;

          // Get base rate from fees
          const feesAndCharges = res.data.data.feesAndCharge || {};
          this.baseRate = parseFloat(feesAndCharges.feeAmount || this.currentAmount);
        } else {
          this.errorMessage = res.data.message || 'Failed to load business details';
        }
      } catch (error) {
        console.error("Error loading business details:", error);
        this.errorMessage = 'An error occurred while loading business details';
      } finally {
        this.loading = false;
      }
    },

    calculateTopUp() {
      if (!this.topUpType) {
        this.calculatedAmount = 0;
        this.totalAmount = 0;
        this.newExpiryDate = null;
        return;
      }

      let amount = 0;
      let months = 0;

      switch (this.topUpType) {
        case 'monthly':
          months = this.topUpPeriod || 1;
          amount = (this.baseRate / 12) * months;
          break;
        case 'quarterly':
          months = (this.topUpPeriod || 1) * 3;
          amount = (this.baseRate / 4) * (this.topUpPeriod || 1);
          break;
        case 'annual':
          months = (this.topUpPeriod || 1) * 12;
          amount = this.baseRate * (this.topUpPeriod || 1);
          break;
        case 'custom':
          amount = this.customAmount || 0;
          months = Math.ceil((amount / this.baseRate) * 12);
          break;
      }

      this.calculatedAmount = Math.round(amount);
      this.additionalFees = 0; // Can add processing fees here if needed
      this.totalAmount = this.calculatedAmount + this.additionalFees;

      // Calculate new expiry date
      if (this.currentExpiryDate) {
        const currentExpiry = moment(this.currentExpiryDate);
        const today = moment();
        const startDate = currentExpiry.isAfter(today) ? currentExpiry : today;
        this.newExpiryDate = startDate.add(months, 'months').format('YYYY-MM-DD');
      } else {
        this.newExpiryDate = moment().add(months, 'months').format('YYYY-MM-DD');
      }
    },

    async processTopUp() {
      if (!this.canSubmit) return;

      this.processing = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const data = new FormData();
        data.append("function", "topUpBusiness");
        data.append("businessID", this.businessID);
        data.append("billNo", this.currentBillNo);
        data.append("feeID", this.feeID);
        data.append("topUpType", this.topUpType);
        data.append("topUpPeriod", this.topUpPeriod);
        data.append("topUpAmount", this.totalAmount);
        data.append("newExpiryDate", this.newExpiryDate);
        data.append("notes", this.notes);
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));
        data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

        const res = await execute(data, trade);
        if (res.data.success) {
          this.successMessage = 'Top up processed successfully! New bill generated.';
          setTimeout(() => {
            this.$emit('success', res.data);
            const modal = bootstrap.Modal.getInstance(document.getElementById('topUpModal'));
            if (modal) modal.hide();
          }, 2000);
        } else {
          this.errorMessage = res.data.message || 'Failed to process top up';
        }
      } catch (error) {
        console.error("Error processing top up:", error);
        this.errorMessage = 'An error occurred while processing top up';
      } finally {
        this.processing = false;
      }
    },

    resetForm() {
      this.topUpType = '';
      this.topUpPeriod = 1;
      this.customAmount = 0;
      this.calculatedAmount = 0;
      this.additionalFees = 0;
      this.totalAmount = 0;
      this.newExpiryDate = null;
      this.notes = '';
      this.errorMessage = '';
      this.successMessage = '';
    },

    numberFormat(num) {
      return new Intl.NumberFormat("en-GB").format(num || 0);
    },

    formattedDate(date) {
      return moment(date).format('DD MMM YYYY');
    },

    initModal() {
      this.loadBusinessDetails();
    }
  },
  watch: {
    businessID(newVal) {
      if (newVal) {
        this.initModal();
      }
    }
  }
};
</script>

<style scoped>
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}

.bg-soft {
  background-color: rgba(var(--bs-info-rgb), 0.1) !important;
}
</style>